<template>
    <div :class="['user-menu', { open: isMenuOpen }]">
        <CagButton no-border class="toggle" @click.native="toggleMenu">
            <span class="label">Menu</span>
            <transition name="icon-control" mode="out-in">
                <IconBase
                    v-if="isMenuOpen"
                    key="close"
                    icon-name="close"
                    size="30"
                    aria-label="close navigation menu"
                    class="icon icon--close"
                />
                <IconBase
                    v-else
                    key="hamburger"
                    icon-name="hamburger-menu"
                    class="icon icon--hamburger"
                    aria-label="open navigation menu"
                />
            </transition>
        </CagButton>

        <div class="dropdown-container">
            <transition name="menu-slide-down">
                <div v-if="isMenuOpen" class="dropdown">
                    <div v-if="accountLabel" class="account-label">
                        <span>{{ accountLabel }}</span>
                    </div>

                    <div :class="['links', linksClass]">
                        <template v-for="(link, index) in visibleLinks">
                            <UserMenuButton
                                :key="`${link.routeName} ${index}`"
                                :route-name="link.routeName"
                                :icon-name="link.iconName"
                                @click.native="toggleMenu"
                            >
                                {{ link.label }}
                            </UserMenuButton>
                        </template>
                    </div>

                    <CagButton class="sign-out-button" @click.native="signOut">
                        <span>Sign Out</span>
                    </CagButton>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { addUserToSentry } from '@/plugins/sentry';

import CagButton from '@/components/global/cag-button';
import IconBase from '@/components/global/icon-base';
import UserMenuButton from '@/components/header/user-menu-button';

export default {
    name: 'UserMenu',

    components: {
        CagButton,
        IconBase,
        UserMenuButton,
    },

    data() {
        return {
            isMenuOpen: false,
            links: [
                {
                    routeName: 'hub',
                    iconName: 'home',
                    label: 'MyChapman Hub',
                },
                {
                    routeName: 'my-vehicles',
                    iconName: 'favorite',
                    label: 'My Vehicles',
                },
                {
                    routeName: 'account',
                    iconName: 'user',
                    label: 'My Account',
                },
                {
                    routeName: 'privacy-policy',
                    iconName: 'privacy',
                    label: 'Privacy Policy',
                },
            ],
        };
    },

    computed: {
        ...mapGetters({
            isEmployee: 'user/isEmployee',
            userEmail: 'user/userEmail',
            userName: 'user/userName',
        }),
        accountLabel() {
            return this.userName || this.userEmail;
        },
        linksClass() {
            const oddEven = this.visibleLinks.length % 2 === 1 ? 'odd' : 'even';
            return `links-${oddEven}`;
        },
        visibleLinks() {
            return this.links.filter(link => {
                if (link.routeName === 'hub' && !this.isEmployee) {
                    return false;
                } else {
                    return true;
                }
            });
        },
    },

    methods: {
        handleClickAway({ target }) {
            const offMenu = !target.closest('.dropdown, .toggle');
            if (offMenu) this.isMenuOpen = false;
        },
        signOut() {
            // The order of these method calls is required to prevent prop errors

            const returnTo =
                process.env.NODE_ENV === 'production'
                    ? 'https://mychapman.chapmanauto.com/auth/sign-in'
                    : 'http://localhost:8181/auth/sign-in';

            this.$auth0.logout({ returnTo });

            this.isMenuOpen = false;

            if (process.env.NODE_ENV === 'production') {
                // Clear user from Sentry scope
                addUserToSentry(null);
            }
        },
        toggleMenu() {
            this.isMenuOpen = !this.isMenuOpen;

            if (this.isMenuOpen) {
                window.addEventListener('mousedown', this.handleClickAway);
            } else {
                window.removeEventListener('mousedown', this.handleClickAway);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.user-menu {
    .toggle {
        display: flex;
        align-items: center;
        padding: 0.5rem 0.5rem 0.5rem 0.75rem;
        font-size: 1.25rem;
        user-select: none;
        cursor: pointer;
        text-align: right;
        transition: $transition-fast;
        text-transform: initial;

        .icon--hamburger {
            margin: 3px 3px 3px 11px;
        }

        .icon--close {
            margin-left: 8px;
            color: $color-error;
        }
    }

    // Prevents dropdown shadow from overflowing
    .dropdown-container {
        overflow: hidden;
        position: absolute;
        right: 0;
        left: 0;
        top: 100%;
        min-height: calc(100vh - 100%);
        pointer-events: none;
    }

    .dropdown {
        display: flex;
        flex-direction: column;
        max-width: 600px;
        max-height: 100%;
        padding: 1rem;
        border-top: 1px solid $color-gray-light;
        border-bottom-right-radius: 0.5rem;
        border-bottom-left-radius: 0.5rem;
        position: absolute;
        top: 0;
        right: 1rem;
        overflow: auto;
        background-color: $color-white;
        box-shadow: $shadow-3;
        pointer-events: all;

        & > * + * {
            margin-top: 1rem;
        }
    }

    .account-label {
        text-align: center;
        padding-bottom: 1rem;
        border-bottom: 1px solid $color-gray-light;
        font-weight: $font-weight-bold;
    }

    .links {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 1rem;

        & > * {
            flex-grow: 0;
            flex-shrink: 1;
            flex-basis: calc(30% - 0.5rem);
        }

        &.links-even > * {
            flex-basis: calc(50% - 0.5rem);
        }
    }
}

@media screen and (max-width: 600px) {
    .user-menu {
        .dropdown {
            right: 0;
            left: 0;
        }
    }
}
</style>
